import React from "react";
import { structuredValue, presentedStatus } from "../utils/tablePresentation";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useOrg } from "../context/OrgContext";
import { useTerminology } from "../context/TerminologyContext";
import {
  DateRangePicker,
  usePagination,
  Pager,
} from "../components/ui-helpers";
import PageHeader from "../components/PageHeader";
import { formatDateTime } from "../utils/datetime";
import { downloadBlob } from "../utils/download";

function displayActor(value) {
  return String(value || "")
    .trim()
    .toLowerCase() === "queue_worker"
    ? "System"
    : structuredValue(value);
}

function statusClass(value) {
  const raw = String(value || "").toLowerCase();
  if (raw === "approved") return "status-approved";
  if (raw === "rejected" || raw === "invalidated") return "status-rejected";
  if (raw === "queued") return "status-queued";
  if (raw === "on hold" || raw === "onhold") return "status-onhold";
  return "status-pending";
}

// Vendor-facing display: real Queued/On Hold internal statuses are masked to a
// single neutral "Submitted" label so vendors don't see queue/quota state.
function vendorDisplayStatus(value) {
  const raw = String(value || "").toLowerCase();
  if (
    raw === "queued" ||
    raw === "on hold" ||
    raw === "onhold" ||
    raw === "initiated"
  )
    return "Submitted";
  return structuredValue(value);
}

function vendorStatusClass(value) {
  const raw = String(value || "").toLowerCase();
  if (
    raw === "queued" ||
    raw === "on hold" ||
    raw === "onhold" ||
    raw === "initiated"
  )
    return "status-pending";
  return statusClass(value);
}

function pctClassForUsage(pct, yellow, red) {
  const p = Number(pct) || 0;
  if (p >= Number(red || 90)) return "usage-pct usage-pct-red";
  if (p >= Number(yellow || 70)) return "usage-pct usage-pct-yellow";
  return "usage-pct usage-pct-normal";
}

function UsageLine({ status }) {
  if (!status) return null;
  const mode = String(status.quota_mode || "both").toLowerCase();
  const yellow = status.yellow_threshold_pct ?? 70;
  const red = status.red_threshold_pct ?? 90;
  const showMonthly = mode === "monthly" || mode === "both";
  const showYearly = mode === "yearly" || mode === "both";

  const monthlyText = showMonthly ? (
    <>
      Monthly <strong>{status.monthly?.in_use ?? 0}</strong> /{" "}
      {status.monthly_limit ?? 0} used{" "}
      <span
        className={pctClassForUsage(status.monthly?.percent_used, yellow, red)}
      >
        ({Number(status.monthly?.percent_used || 0).toFixed(1)}%)
      </span>
    </>
  ) : null;

  const yearlyText = showYearly ? (
    <>
      Yearly <strong>{status.yearly?.in_use ?? 0}</strong> /{" "}
      {status.yearly_limit ?? 0} used{" "}
      <span
        className={pctClassForUsage(status.yearly?.percent_used, yellow, red)}
      >
        ({Number(status.yearly?.percent_used || 0).toFixed(1)}%)
      </span>
    </>
  ) : null;

  return (
    <div className="usage-summary-line">
      <span className="usage-summary-label">Usage:</span> {monthlyText}
      {showMonthly && showYearly ? (
        <span className="usage-summary-sep"> • </span>
      ) : null}
      {yearlyText}
      {status.held_count ? (
        <span className="usage-summary-sep">
          {" "}
          • {status.held_count} on hold
        </span>
      ) : null}
    </div>
  );
}

export default function StatusPage() {
  const { user } = useAuth();
  const { current: currentOrg } = useOrg();
  const { t, tp } = useTerminology();
  const isVendor = user?.currentRole === "Vendor";
  const isSuperAdmin = user?.currentRole === "Super Admin";
  const [rawRows, setRows] = React.useState([]);
  const rows = React.useMemo(() => rawRows.map((row) => ({
    ...row, status: presentedStatus(row.status, isSuperAdmin, isVendor, row.failure_kind),
  })), [rawRows, isSuperAdmin, isVendor]);
  const [q, setQ] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [sortBy, setSortBy] = React.useState("last_action_time");
  const [order, setOrder] = React.useState("desc");
  const [typeFilter, setTypeFilter] = React.useState("");
  const [dateDraft, setDateDraft] = React.useState({
    date_from: "",
    date_to: "",
  });
  const [dateRange, setDateRange] = React.useState({
    date_from: "",
    date_to: "",
  });
  const [usage, setUsage] = React.useState(null);
  const [exportError, setExportError] = React.useState("");

  const reload = React.useCallback(() => {
    const params = {
      vendor: isVendor ? user.uid : undefined,
      vendor_view: isVendor ? "1" : undefined,
      q,
      status,
      sort_by: sortBy,
      order,
      currentOrg,
      type: typeFilter || undefined,
      date_from: dateRange.date_from || undefined,
      date_to: dateRange.date_to || undefined,
    };
    api
      .get("/status_table", { params })
      .then((r) => setRows(r.data.rows || []))
      .catch(() => setRows([]));
  }, [
    isVendor,
    user?.uid,
    q,
    status,
    sortBy,
    order,
    typeFilter,
    dateRange,
    currentOrg,
  ]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  // Usage line is for non-vendor roles only.
  React.useEffect(() => {
    if (isVendor) {
      setUsage(null);
      return;
    }
    api
      .get("/usage/status")
      .then((r) => setUsage(r.data?.status || null))
      .catch(() => setUsage(null));
  }, [isVendor, currentOrg]);

  const pg = usePagination(rows, 25);

  const downloadExport = async (extension) => {
    const params = {};
    if (isVendor) {
      params.vendor = user.uid;
      params.vendor_view = "1";
    }
    if (q) params.q = q;
    if (status) params.status = status;
    if (typeFilter) params.type = typeFilter;
    if (dateRange.date_from) params.date_from = dateRange.date_from;
    if (dateRange.date_to) params.date_to = dateRange.date_to;
    params.sort_by = sortBy;
    params.order = order;
    setExportError("");
    try {
      const res = await api.get(`/export/status.${extension}`, {
        params,
        responseType: "blob",
      });
      downloadBlob(`status.${extension}`, res.data);
    } catch (_) {
      setExportError(`Could not download status ${extension === "xlsx" ? "Excel" : "CSV"}.`);
    }
  };

  const fmtTurnaround = (value) => {
    if (structuredValue(value) === "NA") return "NA";
    const n = Number(value);
    return Number.isFinite(n) ? String(Math.trunc(n)) : (value ?? "");
  };

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Operations"
        icon="status"
        title="Status"
        subtitle="Track uploads, reviews, approvals, and invalidations with filters."
      />

      {!isVendor ? <UsageLine status={usage} /> : null}

      <div className="section">
        <div className="filters-bar status-toolbar">
          <label className="field-grow">
            <span>Search</span>
            <input
              placeholder={`Search ${t("vendor").toLowerCase()}, ${t("material").toLowerCase()}, SKU, ${t("lot").toLowerCase()}, or user`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
          <label>
            <span>Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {!isVendor ? <option>Queued</option> : null}
              {!isVendor ? <option>On Hold</option> : null}
              {isSuperAdmin ? <option>Failed</option> : null}
              <option>Initiated</option>
              <option>Pending</option>
              <option>Approved</option>
              <option>Rejected</option>
              <option>Invalidated</option>
            </select>
          </label>
          <label>
            <span>Sort by</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="last_action_time">Last action</option>
              <option value="upload_time">Uploaded</option>
              <option value="vendor">{t("vendor")}</option>
              <option value="sku">SKU</option>
              <option value="lot_no">{t("lot")}</option>
              <option value="status">Status</option>
              <option value="type">{t("type")}</option>
            </select>
          </label>
          <label>
            <span>Order</span>
            <select value={order} onChange={(e) => setOrder(e.target.value)}>
              <option value="desc">Desc</option>
              <option value="asc">Asc</option>
            </select>
          </label>
          <label>
            <span>{t("type")}</span>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">All {tp("type").toLowerCase()}</option>
              {[...new Set(rows.map((row) => row.type).filter(Boolean))].map(
                (type) => (
                  <option key={type}>{type}</option>
                ),
              )}
            </select>
          </label>
          <DateRangePicker
            value={dateDraft}
            onChange={setDateDraft}
            onApply={() => setDateRange({ ...dateDraft })}
            onClear={(empty) => setDateRange(empty)}
          />
          <div className="status-toolbar-actions">
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => downloadExport("csv")}
            >
              Download CSV
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => downloadExport("xlsx")}
            >
              Download Excel
            </button>
          </div>
        </div>

        {exportError ? <div className="banner error">{exportError}</div> : null}

        {!rows || rows.length === 0 ? (
          <div>No activity yet.</div>
        ) : (
          <div className="table--card">
            <div className="table-scroll">
              <table className="table table--dense table--status">
                <colgroup>
                  <col className="status-col-user" />
                  <col className="status-col-vendor" />
                  <col className="status-col-material" />
                  <col className="status-col-sku" />
                  <col className="status-col-lot" />
                  <col className="status-col-uploaded" />
                  <col className="status-col-status" />
                  <col className="status-col-action" />
                  <col className="status-col-turnaround" />
                  <col className="status-col-type" />
                </colgroup>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>{t("vendor")}</th>
                    <th>{t("material")}</th>
                    <th>SKU</th>
                    <th>{t("lot")}</th>
                    <th>Uploaded</th>
                    <th>Status</th>
                    <th>Last action</th>
                    <th>Turnaround</th>
                    <th>{t("type")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pg.pageData.map((row, index) => {
                    const displayStatus = isVendor
                      ? vendorDisplayStatus(row.status)
                      : structuredValue(row.status);
                    const displayClass = isVendor
                      ? vendorStatusClass(row.status)
                      : statusClass(row.status);
                    return (
                      <tr
                        key={`${row.vendor || "vendor"}-${row.sku || "sku"}-${row.lot_no || index}`}
                      >
                        <td>
                          <span className="status-primary-text">
                            {displayActor(row.user)}
                          </span>
                        </td>
                        <td>
                          <span className="status-primary-text">
                            {structuredValue(row.vendor)}
                          </span>
                        </td>
                        <td>
                          <div
                            className="status-material-cell"
                            title={row.material || ""}
                          >
                            {structuredValue(row.material)}
                          </div>
                        </td>
                        <td>{structuredValue(row.sku)}</td>
                        <td>{structuredValue(row.lot_no)}</td>
                        <td>
                          <div className="status-date-cell">
                            {structuredValue(formatDateTime(row.upload_time))}
                          </div>
                        </td>
                        <td>
                          <span className={`status-pill ${displayClass}`}>
                            {displayStatus}
                          </span>
                        </td>
                        <td>
                          <div className="status-date-cell">
                            {structuredValue(formatDateTime(row.last_action_time))}
                          </div>
                        </td>
                        <td>{fmtTurnaround(row.turnaround_time)}</td>
                        <td>{structuredValue(row.type)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pager {...pg} />
          </div>
        )}
      </div>
    </div>
  );
}
